import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import type { GroupItem } from '@/components/grouped-list'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '../ui/spinner'
import { SpheraFlagSelect } from './sphera-flag-select'

export type Typology = GroupItem

export interface TypologyFormProps {
	mode: 'create' | 'edit'
	initial?: Typology
	onSave: (data: {
		name: string
		spheraName: string | null
		risks: string[]
	}) => void
	onClose: () => void
}

export function TypologyFormContent({
	mode,
	initial,
	onSave,
	onClose,
}: TypologyFormProps) {
	const { t } = useTranslation()
	const [disableSaving, setDisableSaving] = useState(false)
	const [name, setName] = useState(initial?.name ?? '')
	const [spheraName, setSpheraName] = useState(initial?.spheraName ?? '')
	const [risks, setRisks] = useState<string[]>(
		initial?.children.map((r) => r.name) ?? [''],
	)

	const addRisk = () => setRisks((prev) => [...prev, ''])
	const removeRisk = (i: number) =>
		setRisks((prev) => prev.filter((_, idx) => idx !== i))
	const updateRisk = (i: number, value: string) =>
		setRisks((prev) => prev.map((r, idx) => (idx === i ? value : r)))

	const handleSave = async () => {
		if (disableSaving) return

		if (!name.trim()) {
			toast.error(t('administration.typologyRisksForm.emptyTypologyError'))
			return
		}
		const filled = risks.filter((r) => r.trim())
		if (filled.length === 0) {
			toast.error(t('administration.typologyRisksForm.emptyRiskError'))
			return
		}
		setDisableSaving(true)

		try {
			await Promise.resolve(
				onSave({
					name: name.trim(),
					spheraName: spheraName || null,
					risks: filled,
				}),
			)
		} catch (error) {
			toast.error(`Erro ao salvar ${String(error)}`)
		}
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-5 px-1">
			{/* Typology name */}
			<Field>
				<FieldLabel htmlFor="typology-name">
					{t('administration.typologyRisksForm.typologyLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="typology-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t(
							'administration.typologyRisksForm.typologyPlaceholder',
						)}
					/>
				</FieldContent>
			</Field>

			<SpheraFlagSelect value={spheraName} onChange={setSpheraName} />

			<Separator className="bg-border/60" />

			<div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
				{/* Risks list */}
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<span className="text-sm font-semibold text-kaefer-dark-grey">
							{t('administration.typologyRisks.risks')}
						</span>
						<Button
							type="button"
							variant="ghost"
							size="xs"
							onClick={addRisk}
							className="gap-1.5 text-kaefer-medium-grey hover:text-kaefer-dark-grey"
						>
							<Plus className="size-3.5" />
							{t('administration.typologyRisks.addRisk')}
						</Button>
					</div>

					<div className="space-y-2">
						{risks.map((risk, i) => (
							<div key={i} className="flex items-center gap-2">
								<span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-kaefer-medium-grey/10 text-xs font-bold text-kaefer-medium-grey">
									{i + 1}
								</span>
								<Input
									value={risk}
									onChange={(e) => updateRisk(i, e.target.value)}
									placeholder={t(
										'administration.typologyRisksForm.riskPlaceholder',
									)}
									className="h-9 flex-1"
								/>
								{risks.length > 1 && (
									<Button
										type="button"
										variant="ghost"
										size="icon-xs"
										onClick={() => removeRisk(i)}
										className="shrink-0 text-muted-foreground hover:text-kaefer-red"
									>
										<X className="size-3.5" />
									</Button>
								)}
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Footer buttons */}
			<div className="flex shrink-0 gap-3 pt-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
				>
					{t('administration.typologyRisksForm.cancelButton')}
				</Button>
				<Button
					type="button"
					disabled={disableSaving}
					onClick={handleSave}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{mode === 'create' ? (
						disableSaving ? (
							<>
								<Spinner /> {t('administration.typologyRisksForm.saving')}
							</>
						) : (
							t('administration.typologyRisksForm.saveButton')
						)
					) : disableSaving ? (
						<>
							<Spinner /> {t('administration.typologyRisksForm.saving')}
						</>
					) : (
						t('administration.typologyRisks.saveEdit')
					)}
				</Button>
			</div>
		</div>
	)
}
